import { useState } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/app";
import { Plus, Trash, NotePencil } from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

export default function Notes({ storeId }) {
  const { data: notes = [] } = useSWR(`/stores/${storeId}/notes`, fetcher);
  const [text, setText] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post("/notes", { store_id: storeId, period_from: from || null, period_to: to || null, text });
      setText("");
      setFrom("");
      setTo("");
      mutate(`/stores/${storeId}/notes`);
      toast.success("Poznámka uložená");
    } catch (err) {
      toast.error("Chyba pri ukladaní");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Zmazať poznámku?")) return;
    await api.delete(`/notes/${id}`);
    mutate(`/stores/${storeId}/notes`);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5">
      <div className="space-y-3">
        <div className="card-flat p-4 text-sm text-[var(--text-secondary)]">
          <div className="flex items-start gap-2">
            <NotePencil size={18} className="text-[var(--brand)] mt-0.5" weight="duotone" />
            <p>
              Pridajte svoje úvahy, kontext, poznámky o sezónnosti, akciách, výpadkoch personálu. AI ich zoberie do úvahy pri ďalšej záverečnej správe.
            </p>
          </div>
        </div>

        {notes.length === 0 && <div className="card-flat p-6 text-center text-[var(--text-secondary)]">Zatiaľ žiadne poznámky.</div>}

        {notes.map((n) => (
          <div key={n.id} className="card-flat p-4" data-testid={`note-${n.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                {(n.period_from || n.period_to) && (
                  <p className="label-eyebrow mb-2 tabular">
                    Obdobie: {n.period_from || "—"} → {n.period_to || "—"}
                  </p>
                )}
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{n.text}</p>
                <p className="text-xs text-[var(--text-muted)] mt-2 tabular">{new Date(n.created_at).toLocaleString("sk-SK")}</p>
              </div>
              <button onClick={() => remove(n.id)} className="text-[var(--text-muted)] hover:text-[var(--critical)] p-1" aria-label="Zmazať" data-testid={`delete-note-${n.id}`}>
                <Trash size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={submit} className="card-flat p-5 h-fit lg:sticky lg:top-6 space-y-3">
        <h3 className="font-display text-sm font-semibold">Nová poznámka</h3>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label-eyebrow block mb-1">Od</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full" data-testid="note-from-input" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Do</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-full" data-testid="note-to-input" />
          </div>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Text</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            className="w-full resize-none"
            placeholder="Napr.: V piatok 13.3. bola PR akcia, vyššia tržba bola očakávaná. V júni býva personál na dovolenkách..."
            data-testid="note-text-input"
          />
        </div>
        <button type="submit" disabled={saving || !text.trim()} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="add-note-button">
          <Plus size={14} /> {saving ? "Ukladám..." : "Pridať poznámku"}
        </button>
      </form>
    </div>
  );
}
